import { Entity, EventHandle, Vec3 } from 'playcanvas';

import { Events } from './events';
import { resolveFrameInterpolationSegment, type FrameInterpolationSegment } from './motion-interpolation';

export type CameraMotionTrackData = {
    /** Keyframe times, expressed in timeline frames (not seconds). Must be strictly increasing. */
    frames: number[];

    /** Flattened xyz positions, length = frames.length * 3. */
    positions: number[];

    /** Flattened xyz camera targets, length = frames.length * 3. */
    targets: number[];
};

export type CameraMotionTargetSet = {
    body: Entity;
};

let activeLookThroughController: CameraMotionController | null = null;

/**
 * Clear the active camera motion look-through controller (if any).
 * Exported for cross-system coordination with scene cameras.
 */
const clearCameraMotionLookThrough = () => {
    if (activeLookThroughController) {
        activeLookThroughController.setLookThrough(false);
    }
};

const getActiveLookThroughController = () => {
    return activeLookThroughController;
};

const validateTrack = (track: CameraMotionTrackData): string | null => {
    if (!track || typeof track !== 'object') {
        return 'Track is missing.';
    }

    if (!Array.isArray(track.frames) || track.frames.length < 1) {
        return 'Track has no keyframes.';
    }

    if (!Array.isArray(track.positions)) {
        return 'Track positions missing.';
    }

    if (!Array.isArray(track.targets)) {
        return 'Track targets missing.';
    }

    if (track.positions.length !== track.frames.length * 3) {
        return `Track positions length mismatch (expected ${track.frames.length * 3}, got ${track.positions.length}).`;
    }

    if (track.targets.length !== track.frames.length * 3) {
        return `Track targets length mismatch (expected ${track.frames.length * 3}, got ${track.targets.length}).`;
    }

    if (!Number.isFinite(track.frames[0])) {
        return 'Track frames contains non-finite values.';
    }

    for (let i = 1; i < track.frames.length; i++) {
        if (!Number.isFinite(track.frames[i])) {
            return 'Track frames contains non-finite values.';
        }
        if (!(track.frames[i] > track.frames[i - 1])) {
            return `Track frames must be strictly increasing (frames[${i}] <= frames[${i - 1}]).`;
        }
    }

    for (let i = 0; i < track.positions.length; i++) {
        if (!Number.isFinite(track.positions[i])) {
            return `Track positions contains non-finite values (positions[${i}]).`;
        }
    }

    for (let i = 0; i < track.targets.length; i++) {
        if (!Number.isFinite(track.targets[i])) {
            return `Track targets contains non-finite values (targets[${i}]).`;
        }
    }

    return null;
};

const evalVec3Track = (values: number[], frames: number[], frame: number, out: Vec3, scratchSeg: FrameInterpolationSegment) => {
    const { i0, i1, t } = resolveFrameInterpolationSegment(frames, frame, scratchSeg);
    const a = i0 * 3;
    const b = i1 * 3;

    const x0 = values[a];
    const y0 = values[a + 1];
    const z0 = values[a + 2];

    if (i0 === i1 || t <= 0) {
        out.set(x0, y0, z0);
        return;
    }

    if (t >= 1) {
        out.set(values[b], values[b + 1], values[b + 2]);
        return;
    }

    out.set(
        x0 + (values[b] - x0) * t,
        y0 + (values[b + 1] - y0) * t,
        z0 + (values[b + 2] - z0) * t
    );
};

class CameraMotionController {
    private events: Events;
    private targets: CameraMotionTargetSet;
    private track: CameraMotionTrackData;
    private enabled = true;

    // Public for simple Scene Tree toggle UI checks in C.7.
    // Implemented as an accessor so external assignments cannot bypass the
    // exclusivity + listener ordering logic in setLookThrough().
    private _lookThrough = false;

    get lookThrough() {
        return this._lookThrough;
    }

    set lookThrough(value: boolean) {
        this.setLookThrough(value);
    }

    private scratchPos = new Vec3();
    private scratchTarget = new Vec3();
    private scratchWorldTarget = new Vec3();
    private scratchSeg: FrameInterpolationSegment = { i0: 0, i1: 0, t: 0 };

    private lookThroughPose = {
        position: new Vec3(),
        target: new Vec3()
    };

    private timeHandle: EventHandle | null = null;
    private frameHandle: EventHandle | null = null;
    private clearHandle: EventHandle | null = null;

    constructor(events: Events, targets: CameraMotionTargetSet, track: CameraMotionTrackData) {
        this.events = events;
        this.targets = targets;
        this.track = track;

        const error = validateTrack(track);
        if (error) {
            console.warn(`CameraMotionController: invalid track:\n - ${error}`);
            this.enabled = false;
            return;
        }

        this.subscribeTimelineHandlers();

        this.clearHandle = events.on('scene.clear', () => {
            this.destroy();
        });

        const currentFrame = events.invoke('timeline.frame') as number;
        this.evaluate((typeof currentFrame === 'number' && Number.isFinite(currentFrame)) ? currentFrame : 0);
    }

    getTrack(): CameraMotionTrackData {
        return this.track;
    }

    setEnabled(value: boolean) {
        this.enabled = value;
        if (!this.enabled) {
            this.setLookThrough(false);
        }
    }

    setLookThrough(value: boolean) {
        const next = !!value;
        if (next === this.lookThrough) {
            return;
        }

        if (next && !this.enabled) {
            return;
        }

        if (!next) {
            this._lookThrough = false;
            if (activeLookThroughController === this) {
                activeLookThroughController = null;
            }
            this.events.fire('cameraPlayback.refresh');
            return;
        }

        if (activeLookThroughController && activeLookThroughController !== this) {
            activeLookThroughController.setLookThrough(false);
        }

        // Disable any scene camera look-through (cross-system)
        this.events.fire('sceneCamera.clearLookThrough');

        activeLookThroughController = this;
        this._lookThrough = true;

        const currentFrame = this.events.invoke('timeline.frame') as number;
        this.evaluate((typeof currentFrame === 'number' && Number.isFinite(currentFrame)) ? currentFrame : 0);
        this.events.fire('cameraPlayback.refresh');
    }

    sampleLookThroughPose(frame: number) {
        if (!this.enabled || !this.lookThrough) {
            return null;
        }

        this.evaluate(frame);
        return {
            position: this.lookThroughPose.position.clone(),
            target: this.lookThroughPose.target.clone()
        };
    }

    destroy() {
        this.unsubscribeTimelineHandlers();
        this.clearHandle?.off();
        this.clearHandle = null;

        if (activeLookThroughController === this) {
            activeLookThroughController = null;
        }

        this._lookThrough = false;
        this.enabled = false;
        this.events.fire('cameraPlayback.refresh');
    }

    private subscribeTimelineHandlers() {
        this.timeHandle = this.events.on('timeline.time', (time: number) => {
            this.evaluate(time);
        });

        this.frameHandle = this.events.on('timeline.frame', (frame: number) => {
            if (this.events.invoke('timeline.playing') === true) {
                return;
            }

            this.evaluate(frame);
        });
    }

    private unsubscribeTimelineHandlers() {
        this.timeHandle?.off();
        this.frameHandle?.off();
        this.timeHandle = null;
        this.frameHandle = null;
    }

    private evaluate(frame: number) {
        if (!this.enabled) return;

        evalVec3Track(this.track.positions, this.track.frames, frame, this.scratchPos, this.scratchSeg);
        evalVec3Track(this.track.targets, this.track.frames, frame, this.scratchTarget, this.scratchSeg);

        this.targets.body.setLocalPosition(this.scratchPos);

        // Keep body helper oriented toward the animated target.
        const parent = this.targets.body.parent;
        if (parent) {
            parent.getWorldTransform().transformPoint(this.scratchTarget, this.scratchWorldTarget);
        } else {
            this.scratchWorldTarget.copy(this.scratchTarget);
        }

        if (this.scratchWorldTarget.distance(this.targets.body.getPosition()) > 1e-6) {
            this.targets.body.lookAt(this.scratchWorldTarget);
        }

        if (this.lookThrough) {
            // camera.setPose expects world-space coordinates.
            this.lookThroughPose.position.copy(this.targets.body.getPosition());
            this.lookThroughPose.target.copy(this.scratchWorldTarget);
        }
    }
}

export { CameraMotionController, clearCameraMotionLookThrough, getActiveLookThroughController };
